import React, { useState, useEffect } from 'react';
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Row,
  Col,
} from 'reactstrap';
import { categoriasService } from '../../services/categoriasService';
import { empresasService } from '../../services/empresasService';
import { sucursalesService } from '../../services/sucursalesService';

function CategoriaForm({ categoria, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    empresaID: '',
    nombre: '',
    descripcion: '',
    activo: true,
    todasLasSucursales: true,
    sucursalID: null,
  });

  const [empresas, setEmpresas] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadEmpresas();
  }, []);

  useEffect(() => {
    if (formData.empresaID) {
      loadSucursales();
    } else {
      setSucursales([]);
    }
  }, [formData.empresaID]);

  useEffect(() => {
    if (categoria) {
      const sucursalId = categoria.SucursalID || categoria.sucursalID;
      setFormData({
        empresaID: categoria.EmpresaID || categoria.empresaID || '',
        nombre: categoria.Nombre || categoria.nombre || '',
        descripcion: categoria.Descripcion || categoria.descripcion || '',
        activo: categoria.Activo !== undefined ? categoria.Activo : (categoria.activo !== undefined ? categoria.activo : true),
        todasLasSucursales: !sucursalId,
        sucursalID: sucursalId || null,
      });
      if (categoria.EmpresaID || categoria.empresaID) {
        loadSucursales();
      }
    }
  }, [categoria]);

  const loadEmpresas = async () => {
    try {
      setLoadingEmpresas(true);
      const response = await empresasService.getAll();
      if (response.success && response.data) {
        const empresasData = Array.isArray(response.data) ? response.data : [];
        const normalized = empresasData.map(emp => ({
          EmpresaID: emp.EmpresaID || emp.empresaID,
          RazonSocial: emp.RazonSocial || emp.razonSocial || '',
        }));
        setEmpresas(normalized);
        if (normalized.length === 1 && (!formData.empresaID || formData.empresaID === '')) {
          setFormData(prev => ({ ...prev, empresaID: normalized[0].EmpresaID.toString() }));
        }
      }
    } catch (err) {
      setError('Error al cargar empresas');
    } finally {
      setLoadingEmpresas(false);
    }
  };

  const loadSucursales = async () => {
    try {
      setLoadingSucursales(true);
      const empresaId = parseInt(formData.empresaID);
      if (!empresaId) return;
      
      const response = await sucursalesService.getAll(empresaId);
      if (response.success && response.data) {
        const sucursalesData = Array.isArray(response.data) ? response.data : [];
        const normalized = sucursalesData.map(suc => ({
          SucursalID: suc.SucursalID || suc.sucursalID,
          Nombre: suc.Nombre || suc.nombre || '',
        }));
        setSucursales(normalized);
      }
    } catch (err) {
    } finally {
      setLoadingSucursales(false);
    }
  };

  const validate = () => {
    const newErrors = {};

    // EmpresaID es requerido
    if (!formData.empresaID) {
      newErrors.empresaID = 'La empresa es requerida';
    }

    // Nombre es requerido
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    } else if (formData.nombre.length > 200) {
      newErrors.nombre = 'El nombre no puede exceder 200 caracteres';
    }

    // Descripcion opcional pero con límite
    if (formData.descripcion && formData.descripcion.length > 500) {
      newErrors.descripcion = 'La descripción no puede exceder 500 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      const categoriaData = {
        EmpresaID: parseInt(formData.empresaID),
        SucursalID: formData.todasLasSucursales ? null : (formData.sucursalID || null),
        Nombre: formData.nombre.trim(),
        Descripcion: formData.descripcion?.trim() || null,
      };

      let response;
      if (categoria) {
        // Actualizar
        const categoriaId = categoria.CategoriaID || categoria.categoriaID;
        categoriaData.CategoriaID = categoriaId;
        categoriaData.Activo = formData.activo;
        response = await categoriasService.update(categoriaId, categoriaData);
      } else {
        // Crear
        response = await categoriasService.create(categoriaData);
      }

      if (response.success) {
        onSuccess();
      } else {
        setError(response.Message || response.message || 'Error al guardar la categoría');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert color="danger">{error}</Alert>}

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>
              Empresa <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              name="empresaID"
              value={formData.empresaID}
              onChange={handleChange}
              invalid={!!errors.empresaID}
              disabled={loading || loadingEmpresas || !!categoria || empresas.length <= 1}
              required
            >
              <option value="">Seleccione una empresa</option>
              {empresas.map((emp) => (
                <option key={emp.EmpresaID} value={emp.EmpresaID}>
                  {emp.RazonSocial}
                </option>
              ))}
            </Input>
            {errors.empresaID && (
              <div className="text-danger small">{errors.empresaID}</div>
            )}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label>Sucursales</Label>
            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  name="todasLasSucursales"
                  checked={formData.todasLasSucursales}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      todasLasSucursales: e.target.checked,
                      sucursalID: e.target.checked ? null : prev.sucursalID,
                    }));
                  }}
                  disabled={!formData.empresaID || loadingSucursales}
                />
                <span className="form-check-sign">
                  <span className="check" />
                </span>
                Todas las sucursales
              </Label>
            </FormGroup>
            {!formData.todasLasSucursales && (
              <Input
                type="select"
                value={formData.sucursalID || ''}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    sucursalID: e.target.value ? parseInt(e.target.value) : null,
                  }));
                }}
                disabled={!formData.empresaID || loadingSucursales}
              >
                <option value="">Seleccione una sucursal</option>
                {sucursales.map((suc) => (
                  <option key={suc.SucursalID} value={suc.SucursalID}>
                    {suc.Nombre}
                  </option>
                ))}
              </Input>
            )}
            {formData.empresaID && sucursales.length === 0 && !loadingSucursales && (
              <small className="form-text text-muted">
                No hay sucursales disponibles para esta empresa.
              </small>
            )}
            {loadingSucursales && (
              <small className="form-text text-muted">
                Cargando sucursales...
              </small>
            )}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md="6">
          <FormGroup>
            <Label>
              Nombre <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              invalid={!!errors.nombre}
              maxLength={200}
              required
            />
            {errors.nombre && (
              <div className="text-danger small">{errors.nombre}</div>
            )}
          </FormGroup>
        </Col>
      </Row>

      <FormGroup>
        <Label>Descripción</Label>
        <Input
          type="textarea"
          name="descripcion"
          value={formData.descripcion}
          onChange={handleChange}
          invalid={!!errors.descripcion}
          maxLength={500}
          rows={3}
        />
        {errors.descripcion && (
          <div className="text-danger small">{errors.descripcion}</div>
        )}
      </FormGroup>

      {categoria && (
        <FormGroup check>
          <Label check>
            <Input
              type="checkbox"
              name="activo"
              checked={formData.activo}
              onChange={handleChange}
            />
            <span className="form-check-sign">
              <span className="check" />
            </span>
            Categoría Activa
          </Label>
        </FormGroup>
      )}

      <div className="text-right mt-4">
        <Button color="secondary" onClick={onCancel} className="mr-2">
          Cancelar
        </Button>
        <Button color="primary" type="submit" disabled={loading || loadingEmpresas}>
          {loading ? 'Guardando...' : categoria ? 'Actualizar' : 'Crear'}
        </Button>
      </div>
    </Form>
  );
}

export default CategoriaForm;

